import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import useFormState from '../../hooks/useFormState.js';
import { useAuth } from '../../context/AuthContext.jsx';

const VESSEL_TYPES = ['Container', 'Tanker', 'Bulk Carrier', 'General Cargo', 'RoRo', 'Passenger', 'Other'];

const EMPTY = {
  id: null,
  name: '',
  imo: '',
  mmsi: '',
  vesselType: 'Container',
  grossTonnage: '',
  loaM: '',
  beamM: '',
  draftM: '',
  flag: '',
  agent: '',
  ownerUserId: '',
};

export default function VesselRegistryPage() {
  const { hasCapability } = useAuth();
  const canWrite = hasCapability('operations_write');

  const [vessels, setVessels] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);

  const [portCalls, setPortCalls] = useState([]);
  const [aisBusyId, setAisBusyId] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const { form, setForm, errors, setErrors, setField, clearErrors } = useFormState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [aisMsg, setAisMsg] = useState(null);
  // 8877: customers a vessel can be assigned to (so it appears in their booking form).
  const [customers, setCustomers] = useState([]);

  const load = useCallback(async (term) => {
    const res = await api.listVessels(term);
    setVessels(res || []);
  }, []);

  const loadPortCalls = useCallback(async () => {
    const res = await api.listPortCalls();
    setPortCalls(res || []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await Promise.all([
          load(''),
          loadPortCalls(),
          canWrite ? api.listVesselCustomers().then((c) => setCustomers(c || [])).catch(() => {}) : null,
        ]);
      } catch (err) {
        setLoadError(err.message || 'Failed to load vessels.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load, loadPortCalls, canWrite]);

  const simulateAisDelay = async (call, delayMinutes) => {
    setAisBusyId(call.id);
    setPageMsg(null);
    try {
      const updated = await api.ingestPortCallAis(call.id, { delayMinutes });
      await loadPortCalls();
      setPageMsg({
        type: updated.driftAlert ? 'warning' : 'info',
        text: updated.driftAlert
          ? `AIS update on ${updated.callRef}: ETA drift ${updated.driftMinutes} min exceeds the ${updated.alertThresholdMinutes}-min threshold — dispatcher alerted.`
          : `AIS update on ${updated.callRef}: ETA drift ${updated.driftMinutes} min (within threshold).`,
      });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to ingest AIS update.' });
    } finally {
      setAisBusyId(null);
    }
  };

  const openRegister = () => {
    setErrors({});
    setAisMsg(null);
    setForm(EMPTY);
    setModalOpen(true);
  };

  const openEdit = (v) => {
    setErrors({});
    setAisMsg(null);
    setForm({
      id: v.id,
      name: v.name,
      imo: v.imo,
      mmsi: v.mmsi,
      vesselType: v.vessel_type || 'Container',
      grossTonnage: String(v.gross_tonnage ?? ''),
      loaM: String(v.loa_m ?? ''),
      beamM: String(v.beam_m ?? ''),
      draftM: v.draft_m != null ? String(v.draft_m) : '',
      flag: v.flag || '',
      agent: v.agent || '',
      ownerUserId: v.owner_user_id || '',
    });
    setModalOpen(true);
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Vessel name is required';
    if (!/^\d{7}$/.test(form.imo)) next.imo = 'IMO must be 7 digits';
    if (!/^\d{9}$/.test(form.mmsi)) next.mmsi = 'MMSI must be 9 digits';
    if (!form.grossTonnage || Number(form.grossTonnage) <= 0) next.grossTonnage = 'GT must be positive';
    if (!form.loaM || Number(form.loaM) <= 0) next.loaM = 'LOA must be positive';
    if (!form.beamM || Number(form.beamM) <= 0) next.beamM = 'Beam must be positive';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const fetchFromAis = async () => {
    setAisMsg(null);
    if (!/^\d{9}$/.test(form.mmsi)) {
      setErrors((e) => ({ ...e, mmsi: 'Enter a 9-digit MMSI to fetch from AIS' }));
      return;
    }
    try {
      const res = await api.aisLookup(form.mmsi);
      if (!res?.available) {
        setAisMsg({ type: 'warning', text: 'No AIS record found for that MMSI — enter details manually.' });
        return;
      }
      setForm((f) => ({
        ...f,
        name: f.name || res.name || '',
        vesselType: res.vesselType || f.vesselType,
        grossTonnage: res.grossTonnage != null ? String(res.grossTonnage) : f.grossTonnage,
        loaM: res.loaM != null ? String(res.loaM) : f.loaM,
        beamM: res.beamM != null ? String(res.beamM) : f.beamM,
        draftM: res.draftM != null ? String(res.draftM) : f.draftM,
        flag: res.flag || f.flag,
      }));
      // Fields AIS just filled are no longer in error.
      clearErrors(
        ...['name', 'grossTonnage', 'loaM', 'beamM'].filter((k) => res[k] != null && res[k] !== ''),
        'mmsi'
      );
      setAisMsg({ type: 'success', text: `AIS details loaded for ${res.name}.` });
    } catch (err) {
      setAisMsg({ type: 'danger', text: err.message || 'AIS lookup failed.' });
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setPageMsg(null);
    try {
      const payload = {
        name: form.name.trim(),
        imo: form.imo,
        mmsi: form.mmsi,
        vesselType: form.vesselType || null,
        grossTonnage: Number(form.grossTonnage),
        loaM: Number(form.loaM),
        beamM: Number(form.beamM),
        draftM: form.draftM ? Number(form.draftM) : null,
        flag: form.flag.trim() || null,
        agent: form.agent.trim() || null,
        ownerUserId: form.ownerUserId || null,
      };
      if (form.id) await api.updateVessel(form.id, payload);
      else await api.createVessel(payload);
      setPageMsg({ type: 'success', text: 'Vessel saved to the master registry.' });
      await load(search);
      setModalOpen(false);
    } catch (err) {
      setErrors((p) => ({ ...p, form: err.message || 'Failed to save vessel.' }));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (v) => {
    setPageMsg(null);
    try {
      await api.deleteVessel(v.id);
      await load(search);
      setPageMsg({ type: 'success', text: `${v.name} removed.` });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to delete vessel.' });
    }
  };

  const onSearch = async (e) => {
    e.preventDefault();
    await load(search);
  };

  return (
    <div className="container-fluid py-4" data-testid="vessel-registry-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-ship me-2" aria-hidden="true" />
                Vessel Master Registry
              </h3>
              <p className="text-muted mb-0 small">
                Maintain the central vessel master — IMO/MMSI for AIS linkage, GT, LOA, beam, and agent.
              </p>
            </div>
            {canWrite && (
              <button className="btn btn-primary" onClick={openRegister} data-testid="register-vessel">
                <i className="fas fa-plus me-1" aria-hidden="true" />
                Register Vessel
              </button>
            )}
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="vessel-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="vessel-message">
          {pageMsg.text}
        </div>
      )}

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white pt-4 border-bottom-0 d-flex justify-content-between align-items-center">
          <h5 className="mb-0 text-secondary">
            <i className="fas fa-list-ul me-2" aria-hidden="true" />
            Registered Vessels
          </h5>
          <form className="d-flex" onSubmit={onSearch}>
            <input
              className="form-control form-control-sm me-2"
              placeholder="Search name / IMO / MMSI"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="vessel-search"
            />
            <button className="btn btn-sm btn-outline-secondary" type="submit">
              <i className="fas fa-search" aria-hidden="true" />
            </button>
          </form>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light text-secondary small text-uppercase">
                  <tr>
                    <th>Vessel</th>
                    <th>IMO</th>
                    <th>MMSI</th>
                    <th>Type</th>
                    <th>GT</th>
                    <th>LOA (m)</th>
                    <th>Beam (m)</th>
                    <th>Agent</th>
                    <th>Customer</th>
                    {canWrite && <th aria-label="actions" />}
                  </tr>
                </thead>
                <tbody>
                  {vessels.length === 0 && (
                    <tr>
                      <td colSpan={canWrite ? 10 : 9} className="text-center text-muted py-4">
                        No vessels registered yet.
                      </td>
                    </tr>
                  )}
                  {vessels.map((v) => (
                    <tr key={v.id} data-testid="vessel-row" data-imo={v.imo}>
                      <td className="fw-bold text-dark">{v.name}</td>
                      <td>
                        <code data-testid="vessel-imo">{v.imo}</code>
                      </td>
                      <td>
                        <code>{v.mmsi}</code>
                      </td>
                      <td>{v.vessel_type || '—'}</td>
                      <td data-testid="vessel-gt">{Number(v.gross_tonnage)}</td>
                      <td>{Number(v.loa_m)}</td>
                      <td>{Number(v.beam_m)}</td>
                      <td className="small">{v.agent || '—'}</td>
                      <td className="small" data-testid="vessel-owner">
                        {customers.find((c) => c.id === v.owner_user_id)?.name || (v.owner_user_id ? 'Customer' : 'Shared')}
                      </td>
                      {canWrite && (
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-secondary me-1"
                            onClick={() => openEdit(v)}
                            data-testid="edit-vessel"
                            aria-label={`Edit ${v.name}`}
                          >
                            <i className="fas fa-pen" aria-hidden="true" />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => remove(v)}
                            aria-label={`Delete ${v.name}`}
                          >
                            <i className="fas fa-trash" aria-hidden="true" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Active Port Call Schedule + AIS ETA drift (UC 4fc6e362) */}
      <div className="card border-0 shadow-sm mb-4" data-testid="port-call-section">
        <div className="card-header bg-white pt-4 border-bottom-0 d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0 text-secondary">
              <i className="fas fa-route me-2" aria-hidden="true" />
              Active Port Call Schedule
            </h5>
            <p className="text-muted small mb-0">
              Scheduled vs AIS-estimated ETA. Drift beyond 30 minutes raises a dispatcher alarm.
            </p>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light text-secondary small text-uppercase">
                <tr>
                  <th>Call ID</th>
                  <th>Vessel</th>
                  <th>Target Berth</th>
                  <th>Scheduled ETA</th>
                  <th>AIS Estimated ETA</th>
                  <th>Drift (mins)</th>
                  <th>Status</th>
                  {canWrite && <th className="text-center">AIS</th>}
                </tr>
              </thead>
              <tbody>
                {portCalls.length === 0 && (
                  <tr>
                    <td colSpan={canWrite ? 8 : 7} className="text-center text-muted py-4">
                      No active port calls scheduled.
                    </td>
                  </tr>
                )}
                {portCalls.map((c) => (
                  <tr
                    key={c.id}
                    data-testid="port-call-row"
                    data-ref={c.callRef}
                    className={c.driftAlert ? 'table-warning' : ''}
                  >
                    <td className="fw-bold">{c.callRef}</td>
                    <td>{c.vesselName || '—'}</td>
                    <td className="small">{c.berthName || '—'}</td>
                    <td className="small">{c.scheduledEta ? new Date(c.scheduledEta).toLocaleString() : '—'}</td>
                    <td className="small" data-testid="ais-eta">
                      {c.aisEta ? new Date(c.aisEta).toLocaleString() : '— awaiting AIS —'}
                    </td>
                    <td className="fw-bold" data-testid="drift-mins">
                      {c.driftMinutes == null ? '—' : `${c.driftMinutes > 0 ? '+' : ''}${c.driftMinutes}`}
                    </td>
                    <td>
                      {c.driftAlert ? (
                        <span className="badge bg-danger" data-testid="drift-alarm">
                          <i className="fas fa-triangle-exclamation me-1" aria-hidden="true" />
                          Drift Alarm
                        </span>
                      ) : (
                        <span className="badge bg-success" data-testid="drift-ok">
                          {c.status}
                        </span>
                      )}
                    </td>
                    {canWrite && (
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-outline-warning"
                          onClick={() => simulateAisDelay(c, 45)}
                          disabled={aisBusyId === c.id}
                          data-testid="simulate-ais-delay"
                        >
                          {aisBusyId === c.id && (
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                          )}
                          Simulate AIS Delay
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Register / Edit modal */}
      {modalOpen && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="vessel-modal">
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <form onSubmit={save} noValidate>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-ship me-2 text-primary" aria-hidden="true" />
                      {form.id ? 'Edit Vessel' : 'Register Vessel'}
                    </h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setModalOpen(false)} />
                  </div>
                  <div className="modal-body">
                    {errors.form && <div className="alert alert-danger">{errors.form}</div>}
                    {aisMsg && (
                      <div className={`alert alert-${aisMsg.type} py-2`} role="alert" data-testid="ais-message">
                        {aisMsg.text}
                      </div>
                    )}
                    <div className="row">
                      <div className="col-md-8 mb-3">
                        <label htmlFor="v-name" className="form-label small fw-bold text-muted">
                          Vessel Name <span className="text-danger">*</span>
                        </label>
                        <input
                          id="v-name"
                          className={`form-control form-control-sm${errors.name ? ' is-invalid' : ''}`}
                          value={form.name}
                          onChange={(e) => setField('name', e.target.value)}
                          data-testid="vessel-name"
                        />
                        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                      </div>
                      <div className="col-md-4 mb-3">
                        <label htmlFor="v-type" className="form-label small fw-bold text-muted">
                          Vessel Type
                        </label>
                        <select
                          id="v-type"
                          className="form-select form-select-sm"
                          value={form.vesselType}
                          onChange={(e) => setField('vesselType', e.target.value)}
                          data-testid="vessel-type"
                        >
                          {VESSEL_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="v-imo" className="form-label small fw-bold text-muted">
                          IMO (7 digits) <span className="text-danger">*</span>
                        </label>
                        <input
                          id="v-imo"
                          className={`form-control form-control-sm${errors.imo ? ' is-invalid' : ''}`}
                          value={form.imo}
                          onChange={(e) => setField('imo', e.target.value)}
                          data-testid="vessel-imo-input"
                        />
                        {errors.imo && <div className="invalid-feedback">{errors.imo}</div>}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="v-mmsi" className="form-label small fw-bold text-muted">
                          MMSI (9 digits) <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-sm">
                          <input
                            id="v-mmsi"
                            className={`form-control${errors.mmsi ? ' is-invalid' : ''}`}
                            value={form.mmsi}
                            onChange={(e) => setField('mmsi', e.target.value)}
                            data-testid="vessel-mmsi-input"
                          />
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={fetchFromAis}
                            data-testid="fetch-ais"
                            title="Fetch vessel particulars from AIS by MMSI"
                          >
                            <i className="fas fa-satellite-dish me-1" aria-hidden="true" />
                            AIS
                          </button>
                          {errors.mmsi && <div className="invalid-feedback">{errors.mmsi}</div>}
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-3 mb-3">
                        <label htmlFor="v-gt" className="form-label small fw-bold text-muted">
                          Gross Tonnage <span className="text-danger">*</span>
                        </label>
                        <input
                          id="v-gt"
                          type="number"
                          step="0.1"
                          className={`form-control form-control-sm${errors.grossTonnage ? ' is-invalid' : ''}`}
                          value={form.grossTonnage}
                          onChange={(e) => setField('grossTonnage', e.target.value)}
                          data-testid="vessel-gt-input"
                        />
                        {errors.grossTonnage && <div className="invalid-feedback">{errors.grossTonnage}</div>}
                      </div>
                      <div className="col-md-3 mb-3">
                        <label htmlFor="v-loa" className="form-label small fw-bold text-muted">
                          LOA (m) <span className="text-danger">*</span>
                        </label>
                        <input
                          id="v-loa"
                          type="number"
                          step="0.1"
                          className={`form-control form-control-sm${errors.loaM ? ' is-invalid' : ''}`}
                          value={form.loaM}
                          onChange={(e) => setField('loaM', e.target.value)}
                          data-testid="vessel-loa-input"
                        />
                        {errors.loaM && <div className="invalid-feedback">{errors.loaM}</div>}
                      </div>
                      <div className="col-md-3 mb-3">
                        <label htmlFor="v-beam" className="form-label small fw-bold text-muted">
                          Beam (m) <span className="text-danger">*</span>
                        </label>
                        <input
                          id="v-beam"
                          type="number"
                          step="0.1"
                          className={`form-control form-control-sm${errors.beamM ? ' is-invalid' : ''}`}
                          value={form.beamM}
                          onChange={(e) => setField('beamM', e.target.value)}
                          data-testid="vessel-beam-input"
                        />
                        {errors.beamM && <div className="invalid-feedback">{errors.beamM}</div>}
                      </div>
                      <div className="col-md-3 mb-3">
                        <label htmlFor="v-draft" className="form-label small fw-bold text-muted">
                          Draft (m)
                        </label>
                        <input
                          id="v-draft"
                          type="number"
                          step="0.1"
                          className="form-control form-control-sm"
                          value={form.draftM}
                          onChange={(e) => setField('draftM', e.target.value)}
                          data-testid="vessel-draft-input"
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="v-flag" className="form-label small fw-bold text-muted">
                          Flag
                        </label>
                        <input
                          id="v-flag"
                          className="form-control form-control-sm"
                          value={form.flag}
                          onChange={(e) => setField('flag', e.target.value)}
                          data-testid="vessel-flag"
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="v-agent" className="form-label small fw-bold text-muted">
                          Shipping Line / Agent
                        </label>
                        <input
                          id="v-agent"
                          className="form-control form-control-sm"
                          value={form.agent}
                          onChange={(e) => setField('agent', e.target.value)}
                          data-testid="vessel-agent"
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="v-owner" className="form-label small fw-bold text-muted">
                        Customer
                      </label>
                      <select
                        id="v-owner"
                        className="form-select form-select-sm"
                        value={form.ownerUserId}
                        onChange={(e) => setField('ownerUserId', e.target.value)}
                        data-testid="vessel-owner-select"
                      >
                        <option value="">Shared registry vessel (no customer)</option>
                        {customers.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <div className="form-text">The assigned customer can book this vessel.</div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setModalOpen(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={saving} data-testid="save-vessel">
                      {saving && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      )}
                      <i className="fas fa-save me-1" aria-hidden="true" />
                      Register Vessel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}
